import React from 'react';
import { Train, Route, FileText, Warehouse, BadgePercent } from 'lucide-react';
import DirectServiceTemplate from '../components/DirectServiceTemplate';

const accent = {
  heroBg: 'bg-[radial-gradient(circle_at_top,rgba(16,185,129,0.12),transparent_70%)]',
  pill: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  cta: 'bg-[#0f766e] hover:bg-[#115e59] shadow-emerald-900/15',
  kpiBox: 'bg-emerald-50 border-emerald-100 text-emerald-700',
  polyline: '#0f766e',
  inputFocus: 'focus:border-emerald-400',
  orderCta: 'bg-emerald-600 hover:bg-emerald-700 text-white',
  packageActive: 'border-emerald-200 bg-emerald-50/40',
  toggleOn: 'border-emerald-200 bg-emerald-50/40',
  switchTrack: 'bg-emerald-600 border-emerald-600',
  switchKnobOn: 'bg-white translate-x-0',
  stepBox: 'bg-emerald-50 border-emerald-100 text-emerald-700',
  modalHeader: 'bg-gradient-to-l from-emerald-700 to-teal-600',
};

const meta = {
  Icon: Train,
  badge: 'خدمات مستقیم — ریلی',
  title: 'ریلی',
  subtitle:
    'برای ترانزیت پایدار و اقتصادی در مسیرهای طولانی: واگن/کانتینر ریلی، برنامه‌ریزی ایستگاهی، کنترل نقاط تحویل و اتصال به جاده‌ای برای درب-به-درب.',
  quickTitle: 'ترانزیت پایدار با ظرفیت قابل پیش‌بینی',
  quickDesc: 'مناسب برای بارهای حجیم و برنامه‌محور؛ با کنترل تحویل‌ها و KPI برای کاهش توقف.',
  sla: '۲۴ تا ۴۸ ساعت',
  baseDays: 6,
  baseCost: 100,
  placeholders: {
    origin: 'مثلاً: ایستگاه ریلی بندرعباس',
    destination: 'مثلاً: ایستگاه ریلی تهران',
    cargo: 'مثلاً: مواد اولیه / کانتینر / بار پالت‌شده',
  },
};

const packages = [
  {
    key: 'economy',
    title: 'اقتصادی',
    desc: 'زمان‌بندی منعطف، مناسب بارهای غیرحساس به زمان.',
    badge: 'کم‌هزینه',
    badgeCls: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    baseDays: 7,
    baseCost: 85,
  },
  {
    key: 'standard',
    title: 'استاندارد',
    desc: 'تعادل زمان/هزینه با کنترل نقاط تحویل.',
    badge: 'پیشنهادی',
    badgeCls: 'bg-blue-50 text-blue-700 border-blue-100',
    baseDays: 6,
    baseCost: 100,
  },
  {
    key: 'priority',
    title: 'اولویت‌دار',
    desc: 'اولویت تخصیص ظرفیت و هماهنگی نزدیک‌تر در ایستگاه‌ها.',
    badge: 'زمان کمتر',
    badgeCls: 'bg-amber-50 text-amber-700 border-amber-100',
    baseDays: 5,
    baseCost: 135,
  },
];

const capabilities = [
  {
    title: 'برنامه حرکت و نقاط تحویل',
    desc: 'تعریف نقاط تحویل/تحویل‌گیری و کنترل تغییرات برنامه.',
    items: ['برنامه حرکت', 'کنترل ایستگاهی', 'گزارش ETA', 'اعلان تغییر وضعیت'],
  },
  {
    title: 'واگن/کانتینر و تطبیق اسناد',
    desc: 'کنترل مشخصات واگن/کانتینر و تطبیق بارنامه/اسناد برای کاهش توقف.',
    items: ['بارنامه ریلی', 'تطبیق وزن/حجم', 'SLA تحویل', 'کنترل آسیب'],
  },
  {
    title: 'اتصال درب-به-درب',
    desc: 'اتصال ریلی به جاده‌ای برای تحویل نهایی و خدمات جانبی.',
    items: ['حمل ترکیبی', 'Cross-dock', 'انبارداری', 'خدمات ارزش افزوده'],
  },
];

const documents = [
  {
    title: 'بارنامه ریلی',
    desc: 'اطلاعات فرستنده/گیرنده، ایستگاه‌ها، وزن و شرایط حمل.',
    state: 'الزامی',
    stateCls: 'bg-white text-gray-700 border-gray-100',
    tags: ['اسناد حمل', 'ریلی'],
  },
  {
    title: 'پکینگ لیست / مشخصات بار',
    desc: 'برای تطبیق در نقاط تحویل و کنترل خسارت.',
    state: 'الزامی',
    stateCls: 'bg-white text-gray-700 border-gray-100',
    tags: ['کنترل', 'وزن/حجم'],
  },
  {
    title: 'بیمه‌نامه (پیشنهادی)',
    desc: 'پوشش ریسک خسارت/تأخیر و شرایط خاص.',
    state: 'پیشنهادی',
    stateCls: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    tags: ['بیمه', 'ریسک'],
  },
  {
    title: 'مجوزهای کالا (در صورت نیاز)',
    desc: 'براساس نوع کالا و مسیر.',
    state: 'وابسته به کالا',
    stateCls: 'bg-amber-50 text-amber-700 border-amber-100',
    tags: ['مجوز', 'HS'],
  },
];

const processSteps = [
  { title: 'ثبت RFQ و ظرفیت‌سنجی', desc: 'نوع واگن/کانتینر، وزن/حجم و زمان‌بندی را اعلام کنید.' },
  { title: 'برنامه حرکت و رزرو', desc: 'زمان حرکت و نقاط تحویل با SLA مشخص می‌شود.' },
  { title: 'تحویل در ایستگاه مبدا', desc: 'تحویل بار و کنترل اولیه در ایستگاه.' },
  { title: 'رهگیری ایستگاهی', desc: 'وضعیت عبور از ایستگاه‌ها و ETA در سامانه ثبت می‌شود.' },
  { title: 'تحویل در مقصد و اتصال', desc: 'تحویل در ایستگاه مقصد و در صورت نیاز اتصال به جاده‌ای/انبار.' },
];

const providers = [
  {
    name: 'اپراتور واگن و برنامه حرکت',
    Icon: Route,
    badges: ['برنامه حرکت', 'SLA', 'ترانزیت'],
    note: 'مناسب برای حمل‌های برنامه‌محور و مسیرهای طولانی با نیاز به ظرفیت پایدار.',
    score: 4.5,
    scoreCls: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    response: 'کمتر از ۱۲ ساعت',
    coverage: 'ملی/کریدور',
    capacity: 'بالا',
  },
  {
    name: 'کنترل اسناد و تطبیق',
    Icon: FileText,
    badges: ['چک‌لیست', 'کاهش توقف', 'کنترل خسارت'],
    note: 'کنترل اسناد و اطلاعات بار برای کاهش توقف در ایستگاه‌ها.',
    score: 4.2,
    scoreCls: 'bg-blue-50 text-blue-700 border-blue-100',
    response: 'کمتر از ۱ روز',
    coverage: 'ملی',
    capacity: 'متوسط',
  },
  {
    name: 'انبارداری و اتصال درب-به-درب',
    Icon: Warehouse,
    badges: ['Cross-dock', 'توزیع', 'خدمات ارزش افزوده'],
    note: 'برای تحویل نهایی و خدمات انبار/تجمیع در مقصد.',
    score: 4.1,
    scoreCls: 'bg-gray-50 text-gray-700 border-gray-100',
    response: 'کمتر از ۱ روز',
    coverage: 'تهران/استان‌ها',
    capacity: 'قابل افزایش',
  },
  {
    name: 'تسهیلات و مشوق‌های مسیر (پیشنهادی)',
    Icon: BadgePercent,
    badges: ['تعرفه ترجیحی', 'مشوق', 'پروژه'],
    note: 'در برخی مسیرها مشوق‌ها/تعرفه‌های ترجیحی قابل اعمال است (وابسته به سیاست‌ها).',
    score: 4.0,
    scoreCls: 'bg-amber-50 text-amber-700 border-amber-100',
    response: '—',
    coverage: 'مسیرهای منتخب',
    capacity: '—',
  },
];

const hubs = [
  { name: 'ایستگاه ریلی بندرعباس', lat: 27.2, lng: 56.1, note: 'اتصال بندر به شبکه ریلی' },
  { name: 'ایستگاه ریلی یزد', lat: 31.9, lng: 54.37, note: 'گره میانی کریدور' },
  { name: 'ایستگاه ریلی قزوین', lat: 36.27, lng: 50.0, note: 'نقطه ترانزیت شمالی' },
  { name: 'ایستگاه ریلی تهران', lat: 35.69, lng: 51.4, note: 'تحویل و اتصال به جاده‌ای' },
];

const faqs = [
  { q: 'مزیت اصلی ریلی چیست؟', a: 'پایداری، ظرفیت قابل پیش‌بینی و هزینه مناسب‌تر در مسافت‌های طولانی.' },
  { q: 'چه زمانی ترکیب ریلی + جاده‌ای توصیه می‌شود؟', a: 'برای تحویل درب-به-درب و نقاطی که دسترسی ریلی مستقیم ندارند.' },
  { q: 'کدام موارد باعث توقف می‌شود؟', a: 'عدم تطبیق وزن/اسناد، آماده نبودن محل تحویل در ایستگاه، یا نقص مدارک.' },
  { q: 'برای بارهای خاص چه باید کرد؟', a: 'شرایط ویژه (DG/ابعاد) را در RFQ اعلام کنید تا واگن/فرآیند مناسب تخصیص یابد.' },
];

export default function DirectRail() {
  return (
    <DirectServiceTemplate
      meta={meta}
      accent={accent}
      formDefaults={{
        origin: 'ایستگاه ریلی بندرعباس',
        destination: 'ایستگاه ریلی تهران',
        cargo: 'مواد اولیه (کیسه)',
        incoterm: 'FCA',
        weightKg: 26500,
        volumeM3: 42,
        isExpress: false,
        needsInsurance: true,
        isSpecialHandling: false,
      }}
      packages={packages}
      capabilities={capabilities}
      documents={documents}
      processSteps={processSteps}
      providers={providers}
      hubs={hubs}
      faqs={faqs}
    />
  );
}

